/**
 * Ref-count gate for admin PWA update deferral.
 * Consumers call acquire/release (or useAdminCriticalFlow) while a critical edit/export is active.
 * Confirm/Alert dialogs use document.documentElement.dataset.adminDialogOpen separately.
 */

import { useEffect } from 'react';

const ATTR = 'data-admin-critical-flow';

let criticalFlowCount = 0;

function syncDocumentAttribute(): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (criticalFlowCount > 0) {
    document.documentElement.setAttribute(ATTR, 'true');
  } else {
    document.documentElement.removeAttribute(ATTR);
  }
}

/** Increment the critical-flow refcount (sets document attr while count > 0). */
export function acquireAdminCriticalFlow(): void {
  criticalFlowCount += 1;
  syncDocumentAttribute();
}

/** Decrement the critical-flow refcount (clears document attr when count hits 0). */
export function releaseAdminCriticalFlow(): void {
  criticalFlowCount = Math.max(0, criticalFlowCount - 1);
  syncDocumentAttribute();
}

export function isAdminCriticalFlowActive(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  if (document.documentElement.dataset.adminDialogOpen === 'true') {
    return true;
  }
  if (criticalFlowCount > 0) {
    return true;
  }
  return document.querySelector(`[${ATTR}="true"]`) !== null;
}

/** Keep document-level critical-flow active while `active` is true. */
export function useAdminCriticalFlow(active: boolean): void {
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    acquireAdminCriticalFlow();
    return (): void => {
      releaseAdminCriticalFlow();
    };
  }, [active]);
}

export function resetAdminCriticalFlowGateForTests(): void {
  criticalFlowCount = 0;
  syncDocumentAttribute();
}
